import CurrencyInput from "./CurrencyInput";
import useIsNarrow from "../hooks/useIsNarrow";

/**
 * "Out of Pocket" — what the buyer actually has to bring to the table.
 *
 *   Down Payment + Net Closing Costs + Renovation
 *                + (Time to Renovate x (Monthly Tax + Monthly HOA + Monthly Mortgage))
 *
 * Lifted out of PredictionPage so the client share page (ClientViewPage) shows the SAME box
 * with the SAME arithmetic. It had to be one component rather than two: the total here is the
 * initial investment the IRR is computed from, so a client page that totted it up its own way
 * would quote a return that the analyst's page disagrees with.
 *
 * `readOnly` is what the client page passes. The analyst can type a renovation budget and a
 * renovation timeline, which persist via onUpdate; the client sees the resulting numbers and
 * cannot edit them (there is nowhere to save them to, and they are the analyst's assumptions
 * to make). Down payment follows propertyAnalysis.down_payment_percent in both cases, which is
 * how the client page's own Financial Assumptions control feeds this box.
 */
export default function OutOfPocketSection({
  property, prediction, propertyAnalysis, checklistData, onUpdate, readOnly = false,
}) {
  const narrow = useIsNarrow();

  // Calculate adjusted asking price (including financed other costs)
  const originalPrice = property?.price || property?.asking_price || 0;
  const discountPct = checklistData?.discount_pct ? parseFloat(checklistData.discount_pct) : 0;
  let askingPrice = originalPrice * (1 - discountPct / 100);

  // Add financed other costs to the asking price
  const otherCostsItems = checklistData?.other_costs_items || [];
  const financedCosts = otherCostsItems
    .filter(item => item.payment_type === "financed" && item.cost)
    .reduce((sum, item) => sum + parseFloat(item.cost || 0), 0);
  askingPrice += financedCosts;

  const downPaymentPercent = propertyAnalysis?.down_payment_percent || 25;
  // Use saved down payment amount if available, otherwise calculate from percentage
  const downPayment = propertyAnalysis?.down_payment_amount
    ? Math.round(propertyAnalysis.down_payment_amount)
    : Math.round(askingPrice * (downPaymentPercent / 100));

  // Get carrying costs from property (single source of truth)
  const monthlyTax = property?.monthly_tax || 0;
  const monthlyHOA = property?.monthly_hoa || 0;

  const renovationCost = propertyAnalysis?.renovation_cost || 0;
  const timeToRenovate = propertyAnalysis?.time_to_renovate || 0;
  const netClosingCosts = propertyAnalysis?.net_closing_costs || 0;

  // Calculate monthly mortgage payment
  // Use saved mortgage data if available, otherwise use defaults
  const mortgageAmount = askingPrice - downPayment;

  // Check if we have saved mortgage payment from modal, otherwise calculate default
  let monthlyMortgagePayment = 0;
  if (propertyAnalysis?.monthly_payment) {
    // Use saved monthly payment from mortgage modal
    monthlyMortgagePayment = Math.round(propertyAnalysis.monthly_payment);
    console.log('💰 Using saved monthly mortgage payment:', monthlyMortgagePayment);
  } else {
    // Calculate default: 7% rate, 30-year term, P&I
    const annualInterestRate = 7;
    const monthlyInterestRate = annualInterestRate / 100 / 12;
    const numberOfPayments = 30 * 12;

    monthlyMortgagePayment = mortgageAmount > 0
      ? Math.round(mortgageAmount * (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numberOfPayments)) /
        (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1))
      : 0;
    console.log('💰 Using default calculated monthly mortgage payment:', monthlyMortgagePayment);
  }

  // Formula: Down Payment + Net Closing Costs + Renovation Cost + (Time to Renovate × (Monthly Tax + Monthly HOA + Monthly Mortgage))
  // Only add renovation-related costs if BOTH renovation cost AND time to renovate are > 0
  const renovationRelatedCosts = (renovationCost > 0 && timeToRenovate > 0)
    ? Math.round(renovationCost + (timeToRenovate * (monthlyTax + monthlyHOA + monthlyMortgagePayment)))
    : 0;

  const totalOutOfPocket = Math.round(downPayment + netClosingCosts + renovationRelatedCosts);

  // Handle input changes with debounced save
  const handleRenovationChange = (value) => {
    const updatedAnalysis = {
      ...propertyAnalysis,
      renovation_cost: parseFloat(value) || 0
    };
    onUpdate(updatedAnalysis);
  };

  const handleTimeToRenovateChange = (value) => {
    const updatedAnalysis = {
      ...propertyAnalysis,
      time_to_renovate: parseFloat(value) || 0
    };
    onUpdate(updatedAnalysis);
  };

  const sectionStyle = {
    backgroundColor: "white",
    border: "none",
    borderRadius: "8px",
    padding: narrow ? "16px 12px" : "24px",
    marginBottom: "24px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  };

  const headingStyle = {
    fontSize: "20px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "16px",
    borderBottom: "2px solid #000",
    paddingBottom: "8px",
  };

  const inputGroupStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  };

  const labelStyle = {
    fontSize: "14px",
    fontWeight: "600",
    color: "#333",
  };

  const inputStyle = {
    padding: "10px",
    border: "2px solid #ddd",
    borderRadius: "6px",
    fontSize: "16px",
    fontWeight: "600",
    textAlign: "right",
  };

  return (
    <div style={sectionStyle}>
      <h2 style={headingStyle}>Out of Pocket</h2>

      <div style={{ display: "grid", gridTemplateColumns: narrow ? "1fr" : "repeat(2, 1fr)", gap: "16px", marginBottom: "20px" }}>
        {/* Down Payment (calculated) */}
        <div style={inputGroupStyle}>
          <label style={labelStyle}>Down Payment ({Number(downPaymentPercent).toFixed(2)}%)</label>
          <input
            type="text"
            style={{ ...inputStyle, backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
            value={`$${downPayment.toLocaleString()}`}
            disabled
          />
        </div>

        {/* Renovation */}
        <div style={inputGroupStyle}>
          <label style={labelStyle}>Renovation</label>
          {readOnly ? (
            <input
              type="text"
              style={{ ...inputStyle, backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
              value={`$${Math.round(renovationCost).toLocaleString()}`}
              disabled
            />
          ) : (
            <CurrencyInput
              style={inputStyle}
              value={renovationCost}
              onChange={handleRenovationChange}
            />
          )}
        </div>

        {/* Net Closing Costs (greyed out) */}
        <div style={inputGroupStyle}>
          <label style={labelStyle}>Net Closing Costs</label>
          <input
            type="text"
            style={{ ...inputStyle, backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
            value={`$${Math.round(netClosingCosts).toLocaleString()}`}
            disabled
          />
        </div>

        {/* Time to Renovate */}
        <div style={inputGroupStyle}>
          <label style={labelStyle}>Time to Renovate (months)</label>
          <input
            type="number"
            style={readOnly ? { ...inputStyle, backgroundColor: "#f8f9fa", cursor: "not-allowed" } : inputStyle}
            value={timeToRenovate}
            onChange={(e) => handleTimeToRenovateChange(e.target.value)}
            min="0"
            step="1"
            disabled={readOnly}
          />
        </div>
      </div>

      {/* Total Out of Pocket */}
      <div style={{ padding: narrow ? "16px" : "20px", backgroundColor: "#fff3cd", borderRadius: "8px", border: "2px solid #ffc107" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
          <span style={{ fontSize: narrow ? "16px" : "18px", fontWeight: "700", color: "#333" }}>TOTAL OUT OF POCKET</span>
          <span style={{ fontSize: narrow ? "24px" : "28px", fontWeight: "700", color: "#1e3a8a" }}>
            ${totalOutOfPocket.toLocaleString()}
          </span>
        </div>
        <div style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}>
          Down Payment + Net Closing Costs + Renovation + (Time to Renovate × (Monthly Tax + Monthly HOA + Monthly Mortgage))
        </div>
      </div>
    </div>
  );
}
